import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import type { ComponentProps } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props<T extends FieldValues> extends ComponentProps<
  typeof TextInput
> {
  control: Control<T>;
  fieldName: FieldPath<T>;
  multiline?: boolean;
  disabled?: boolean;
  uppercase?: boolean;
  label?: string;
  alwaysShrinkLabel?: boolean;
  onBlur?: () => void;
}

export const ControllerFormTextInput = <T extends FieldValues>({
  control,
  fieldName,
  label,
  multiline,
  disabled,
  uppercase,
  alwaysShrinkLabel,
  className,
  readOnly,
  onBlur,
  ...props
}: Props<T>) => {
  const { t } = useTranslation();

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({
        field,
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => (
        <TextInput
          {...field}
          {...props}
          value={field.value || ''}
          className={className ?? fullWidth}
          multiline={multiline}
          alwaysShrinkLabel={alwaysShrinkLabel}
          label={label ? label : t(`fields.${field.name}`)}
          error={error && t(`formErrors.${error.message}`)}
          onBlur={() => {
            field.onBlur();
            onBlur?.();
          }}
          inputProps={{
            ...(uppercase ? { style: { textTransform: 'uppercase' } } : {}),
            readOnly: disabled || formDisabled || readOnly,
          }}
        />
      )}
    />
  );
};

const fullWidth = css({
  flex: 1,
});
